import { useCanOpenRoom } from "../../domains/room/hooks/useCanOpenRoom";
import { IconExternalLink } from "@tabler/icons-react";
import { isUserAuthentified } from "../../domains/user/utils/isUserAuthentified";
import { lazy, Suspense, useLayoutEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import ShareV2Icon from "../../assets/icons/share-v2.svg?react";
import { displayToast } from "../../components/app/AppToast";
import ContentHeader from "../../components/app/headers/ContentHeader";
import { RoomJoinOffer } from "../../components/room/DiscoverableRoomBlock";
import { useSharedContentBack } from "../../domains/room/hooks/useSharedContentBack";
import { useShareInfographic } from "../../domains/infographic/hooks/useShareInfographic";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import ItemList from "../../components/app/ItemList";
import ArticlePublicationCard from "../../components/cards/ArticlePublicationCard";
import InfographicCard from "../../components/cards/InfographicCard";

import VideoCard from "../../components/cards/VideoCard";
import FigmaEmbed from "../../components/FigmaEmbed";
import Loader from "../../components/Loader";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { DebugLogger } from "../../domains/app/utils/debugLogger";
import { handleErrorContentNotFound } from "../../domains/app/utils/errorHandler";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventPageView } from "../../tools/analytics/common.analytics";
import { gaEventViewItemListArticle } from "../../tools/analytics/domains/article.analytics";
import { gaEventViewItemInfographic } from "../../tools/analytics/domains/infographic.analytics";
import { useTranslation } from "react-i18next";
import { apiClient, useApiQuery, type Api } from "../../services/api";
import type { ArticleDetail } from "../../features/article/hooks/useArticleData";
import { useCongressAttributionFromNavigation } from "../../domains/congress/congressAttribution";
import { openExternalUrl } from "../../tools/open-external-url";
import { infographicSource } from "../../domains/infographic/infographicSource";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import { DesktopInfographicPage } from "./desktop/DesktopInfographicPage";
import { useInfographicProgress } from "./useInfographicProgress";

type ContentDetail = Api["get"]["/webapp/public/content/:id"]["output"]["body"];
type InfographicDetail = Extract<
  ContentDetail,
  { content_format: "Infographic" }
>;
type LibraryDetailItem = ArticleDetail | ContentDetail;

const debug = new DebugLogger("Infographic Page");

debug.mute();

const LazyPdfViewer = lazy(() => import("../../components/PdfViewer"));

export default function InfographicPage(props: {
  slug: string;
  roomId: string | null;
}) {
  if (isDesktopLayout()) {
    return <DesktopInfographicPage slug={props.slug} roomId={props.roomId} />;
  }
  return <MobileInfographicPage slug={props.slug} roomId={props.roomId} />;
}

function MobileInfographicPage(props: { slug: string; roomId: string | null }) {
  const history = useHistory();
  const params = { slug: props.slug, roomId: props.roomId };
  const { t } = useTranslation();
  const handleBack = useSharedContentBack(props.roomId);
  const { currentUser } = useCurrentUser();
  // The row only dresses the page (logo, analytics). A shared link brings
  // readers the row refuses (no session, or not in a private room), and the
  // refusal would only reach Sentry: ask only when the reader can open it.
  const canOpenRoom = useCanOpenRoom(props.roomId);
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    props.roomId !== null && canOpenRoom === "open"
      ? { param: { id: props.roomId } }
      : false
  );
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  // Stamped by the card that navigated here; null on every other entry.
  const congressAttribution = useCongressAttributionFromNavigation();
  const { triggerShare: shareInfographic } = useShareInfographic(
    AnalyticsItemList.INFOGRAPHIC_PAGE
  );
  const [infographic, setInfographic] = useState<InfographicDetail | null>(
    null
  );
  const [isLoading, setLoading] = useState(true);
  const [isRelatedContentExpanded, setRelatedContentExpanded] = useState(false);
  const infographicContainer = useRef<HTMLDivElement | null>(null);
  const [associatedArticles, setAssociatedArticles] = useState<ArticleDetail[]>(
    []
  );
  const [associatedContent, setAssociatedContent] = useState<ContentDetail[]>(
    []
  );
  const infographicProgress = useInfographicProgress({
    infographic,
    surface: "mobile",
  });
  const handleDragYRef = useRef(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffectOnce(() => {
    void initialize();
  }, []);

  // Measure header height after content is loaded
  useLayoutEffect(() => {
    if (isLoading || !headerRef.current) return;

    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.getBoundingClientRect().height;
        setHeaderHeight(height);
      }
    };

    // Measure immediately after DOM mutations
    updateHeight();

    // Also observe for future changes (window resize, etc.)
    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    resizeObserver.observe(headerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isLoading]);

  async function initialize() {
    try {
      debug.log("STEP 1/3: Initializing page");

      await loadInfographic();
    } catch (error) {
      if (handleErrorContentNotFound(error)) return;
      console.error("Couldn't mount.", error);

      displayToast(t("Something went wrong. Please try again."), "error");
      if (params.roomId !== null) {
        history.replace(`/room/discovery/${params.roomId}`);
      } else {
        history.replace("/discovery");
      }
    }
  }

  async function loadInfographic() {
    try {
      debug.log("STEP 2/3: Loading infographic");

      const { body } = await apiClient.get(
        "/webapp/public/content/slug/:slug",
        {
          param: { slug: params.slug },
        }
      );
      let contentData: InfographicDetail;
      switch (body.content_format) {
        case "Infographic": {
          contentData = body;
          break;
        }
        default: {
          throw new Error(
            `Expected Infographic from /webapp/public/content/slug/:slug, got ${body.content_format}`
          );
        }
      }
      setInfographic(contentData);
      setAssociatedArticles(contentData.associatedArticles);
      void markInfographicAsViewed(contentData);
      void loadAssociatedContent(contentData.id);
    } finally {
      // in case load event is not triggered
      setTimeout(() => {
        setLoading(false);
      }, 10_000);
    }
  }

  async function markInfographicAsViewed(infographic: InfographicDetail) {
    try {
      debug.log("STEP 3/3: Mark infographic as viewed", {
        slug: infographic.slug,
      });

      await gaEventPageView(currentRoom);
      // A shared link opens this page with no session, and the views route is
      // private. Ungated, its refusal threw before the two events below.
      if (await isUserAuthentified()) {
        await apiClient.post("/webapp/private/content/:id/views", {
          param: { id: infographic.id },
        });
      }
      gaEventViewItemInfographic(
        {
          infographic,
          listName: AnalyticsItemList.INFOGRAPHIC_PAGE,
        },
        congressAttribution
      );

      if (infographic.associatedArticles.length > 0) {
        gaEventViewItemListArticle({
          articles: infographic.associatedArticles,
          listName: AnalyticsItemList.INFOGRAPHIC_PAGE,
        });
      }
    } catch {
      // api error already captured by onRequestError.
    }
  }

  async function loadAssociatedContent(contentId: string) {
    try {
      const { body } = await apiClient.get(
        "/webapp/public/content/:id/related-content",
        { param: { id: contentId } }
      );
      const nonArticleContent: ContentDetail[] = [];
      for (const item of body) {
        switch (item.content_format) {
          case "Video":
          case "Infographic": {
            nonArticleContent.push(item);
            break;
          }
          case "Nectar":
          case "Audio": {
            break;
          }
          default: {
            throw new Error(
              "should not happen: unexpected content_format in infographic related content"
            );
          }
        }
      }
      setAssociatedContent(nonArticleContent);
    } catch {
      // api error already captured by onRequestError.
    }
  }

  const relatedContentById = new Map<string, LibraryDetailItem>();
  for (const item of [...associatedArticles, ...associatedContent]) {
    if (item.content_format === "Audio") continue;
    if (!relatedContentById.has(item.id)) {
      relatedContentById.set(item.id, item);
    }
  }
  const relatedContent = [...relatedContentById.values()];

  function handleToggleExpandRelatedContent() {
    setRelatedContentExpanded(!isRelatedContentExpanded);
  }

  return (
    <div className="relative box-border size-full  flex flex-col pt-(--safe-area-top)">
      <div ref={headerRef}>
        <ContentHeader
          onBack={() => {
            infographicProgress.noteExit("back");
            handleBack();
          }}
          // ContentHeader is md:hidden by default; force it visible on desktop
          // too so the resource page keeps a top bar with a back button.
          className="md:block"
          title={t("Infographic")}
          actions={
            infographic !== null ? (
              <div className="flex items-center gap-4">
                {infographic.externalLink !== null && (
                  <IconExternalLink
                    size={24}
                    stroke={1.5}
                    className="cursor-pointer"
                    onClick={() => {
                      if (infographic.externalLink !== null) {
                        openExternalUrl(infographic.externalLink);
                      }
                    }}
                  />
                )}
                {currentUser !== null && (
                  <ShareV2Icon
                    className="cursor-pointer"
                    onClick={() => {
                      infographicProgress.noteExit("share");
                      void shareInfographic(
                        infographic,
                        currentUser,
                        props.roomId
                      );
                    }}
                  />
                )}
              </div>
            ) : null
          }
        />
        {/* In the header, so the viewer below keeps its measured height. */}
        {props.roomId !== null && <RoomJoinOffer roomId={props.roomId} />}
      </div>
      {infographic && (
        <div
          ref={infographicContainer}
          className="box-border flex-1 overflow-hidden flex [&_img]:w-full [&_img]:h-auto md:w-[600px]"
        >
          {isLoading && (
            <div className="fixed top-[calc(54px+var(--safe-area-top))] inset-x-0  bottom-0 flex justify-center items-center bg-[#85858577] z-1 md:left-1/2 md:-translate-x-1/2 md:w-[600px]">
              <Loader expanded />
            </div>
          )}
          {(() => {
            const source = infographicSource(infographic);
            const viewerHeight = `calc(100dvh - ${headerHeight}px - var(--safe-area-top))`;
            switch (source.kind) {
              case "pdf":
                return (
                  <div
                    key={`pdf-${infographic.id}`}
                    ref={(element) => {
                      if (element !== null) {
                        infographicProgress.onPdfScroll(element);
                      }
                    }}
                    onScroll={(event) => {
                      infographicProgress.onPdfScroll(event.currentTarget);
                    }}
                    style={{
                      width: "100%",
                      height: viewerHeight,
                      overflow: "auto",
                      WebkitOverflowScrolling: "touch",
                    }}
                  >
                    <Suspense fallback={null}>
                      <LazyPdfViewer
                        url={source.url}
                        loadingFallback={null}
                        controls={{ kind: "floating" }}
                        display={{ kind: "continuous" }}
                        onLoad={() => {
                          setLoading(false);
                          infographicProgress.onContentLoad();
                        }}
                      />
                    </Suspense>
                  </div>
                );
              case "embed":
                // A figma.site page and our own HTML on juisci.xyz are both
                // HTML pages with no extension: `getFigmaEmbedUrl` passes them
                // through untouched, so this is a plain iframe whose loading
                // state resolves on the native `onLoad`.
                return (
                  <FigmaEmbed
                    key={`embed-${infographic.id}`}
                    url={source.url}
                    onLoad={() => {
                      setLoading(false);
                      infographicProgress.onContentLoad();
                    }}
                  />
                );
              case "image":
                return (
                  <TransformWrapper
                    disablePadding
                    onInit={infographicProgress.onImageView}
                    onTransformed={infographicProgress.onImageView}
                  >
                    <TransformComponent
                      wrapperStyle={{
                        height: viewerHeight,
                        overflowY: "hidden",
                      }}
                    >
                      <img
                        key={`img-${infographic.id}`}
                        src={source.url}
                        alt={infographic.title}
                        loading="lazy"
                        onLoad={() => {
                          setLoading(false);
                          infographicProgress.onContentLoad();
                        }}
                      />
                    </TransformComponent>
                  </TransformWrapper>
                );
              case "none":
                return null;
              default:
                throw new Error(source satisfies never);
            }
          })()}
        </div>
      )}

      {relatedContent.length > 0 && (
        <div
          className="fixed left-0 z-2 w-full h-[calc(90dvh-var(--safe-area-top))] flex flex-col items-center rounded-t-2xl bg-[linear-gradient(180deg,#ff699c_0%,#fe5763_26.99%,#ff8a00_51.99%,#fdb955_77.51%,#ffc408_100%)] transition-[top] duration-300 ease-in-out md:w-[600px] md:left-1/2 md:-translate-x-1/2 [&_.handle]:w-full [&_.handle]:min-h-[64px] [&_.handle]:relative [&_.handle::after]:absolute [&_.handle::after]:top-[calc(50%-2px)] [&_.handle::after]:left-[calc(50%-16px)] [&_.handle::after]:content-[''] [&_.handle::after]:w-8 [&_.handle::after]:h-1 [&_.handle::after]:rounded-2xl [&_.handle::after]:bg-white [&_h2]:m-[0_0_16px] [&_h2]:font-black [&_h2]:text-2xl [&_h2]:text-center [&_h2]:text-white [&_.card-content-container]:gap-0"
          style={{
            top: isRelatedContentExpanded
              ? "calc(10dvh + var(--safe-area-top))"
              : "calc(100dvh - 64px)",
          }}
        >
          <div
            style={{ width: "100%" }}
            onClick={handleToggleExpandRelatedContent}
            onTouchStart={(e) => {
              const touch = e.touches.item(0);
              if (touch !== null) handleDragYRef.current = touch.clientY;
            }}
            onTouchMove={(e) => {
              const touch = e.touches.item(0);
              if (touch === null) return;
              const clientY = touch.clientY;
              if (
                !isRelatedContentExpanded &&
                clientY < handleDragYRef.current
              ) {
                setRelatedContentExpanded(true);
              }
              if (
                isRelatedContentExpanded &&
                clientY > handleDragYRef.current
              ) {
                setRelatedContentExpanded(false);
              }
            }}
          >
            <div className="handle" />
            <h2>{t("Related content")}</h2>
          </div>
          <ItemList className="overflow-y-auto">
            {relatedContent.map((item) => {
              switch (item.content_format) {
                case "Article": {
                  return (
                    <ArticlePublicationCard
                      roomId={props.roomId ?? null}
                      key={item.id}
                      article={item}
                      analyticsListName={AnalyticsItemList.INFOGRAPHIC_PAGE}
                      recommendation={null}
                      onClick={null}
                      className={null}
                      showImage={true}
                    />
                  );
                }
                case "Video": {
                  return (
                    <VideoCard
                      roomId={props.roomId ?? null}
                      key={item.id}
                      video={item}
                      analyticsListName={AnalyticsItemList.INFOGRAPHIC_PAGE}
                      recommendation={null}
                      onClick={null}
                      className={null}
                    />
                  );
                }
                case "Nectar": {
                  return null;
                }
                case "Infographic": {
                  return (
                    <InfographicCard
                      roomId={props.roomId ?? null}
                      key={item.id}
                      infographic={item}
                      analyticsListName={AnalyticsItemList.INFOGRAPHIC_PAGE}
                      recommendation={null}
                      onClick={null}
                      className={null}
                    />
                  );
                }
                case "Audio": {
                  return null;
                }
                default: {
                  throw new Error(item satisfies never);
                }
              }
            })}
          </ItemList>
        </div>
      )}
    </div>
  );
}
